import { Banner, ButtonProps } from '@dfh-corp-sites/ui'
import { graphql } from 'gatsby'
import { GatsbyImage } from 'gatsby-plugin-image'
import { getGatsbyImageData } from 'gatsby-source-sanity'
import React, { useEffect, useMemo } from 'react'

import clientConfig from '../../../../client-config'
import { SanityCta } from '../../../graphql/gatsby'
import { setAnalyticsEvent } from '../../../lib/analytics/state'
import { useSanityRoleAndContextBasedContent } from '../../../lib/sanity'
import SanityLink, { NormalizedSanityLinkTagged } from '../../global/SanityLink'
import ThirdPartySandboxIFrame, {
  ThirdPartySandboxQueryParams,
} from '../../global/ThirdPartySandboxIFrame'
import { BannerVariant, SanityBannerFiltered } from './types'

const setAnalyticsEventOnClick = (
  cta: SanityCta,
  ctaType: 'primary' | 'secondary' | 'tertiary',
  link: NormalizedSanityLinkTagged,
  safeVariant: BannerVariant,
) => {
  setAnalyticsEvent({
    event: 'select_content',
    content_type: 'banner',
    item_id: `banner-${safeVariant}`,
    title: cta.title || '',
    label_text: `${cta.title || ''} (${ctaType})`,
    link_destination: link._tag === 'Normalized' ? link.href ?? undefined : link._tag,
    module_name: 'SanityBanner',
    module_variant: safeVariant,
  })
}

export interface SanityBannerProps extends SanityBannerFiltered {
  className?: string
}

export const SanityBanner: React.FC<SanityBannerProps> = ({
  className,
  variant = 'promo',
  header,
  title,
  description,
  image,
  imageAlt,
  primaryCta,
  primaryCtaAriaLabel,
  secondSectionTitle,
  secondSectionDescription,
  secondaryCta,
  secondaryCtaAriaLabel,
  tertiaryCta,
  tertiaryCtaAriaLabel,
  roleBasedContent,
  contextBasedContent,
  wistiaShareUrl,
  aspectRatio,
  videoCtaText,
}) => {
  const { shouldDisplayContent } = useSanityRoleAndContextBasedContent()
  const shouldDisplay = useMemo(
    () => shouldDisplayContent({ contextBasedContent, roleBasedContent }),
    [contextBasedContent, roleBasedContent, shouldDisplayContent],
  )

  const params = useMemo((): ThirdPartySandboxQueryParams | undefined => {
    if (!wistiaShareUrl) return undefined
    const options = {
      autoPlay: true,
      muted: true,
      plugin: {
        'captions-v1': {
          onByDefault: true,
          showOnControls: true,
        },
      },
      videoFoam: true,
    }

    return {
      r: aspectRatio || '16:9',
      w: wistiaShareUrl,
      wo: JSON.stringify(options),
    }
  }, [aspectRatio, wistiaShareUrl])

  // Handle null values for variant
  const safeVariant: BannerVariant = (variant as BannerVariant) || 'promo'

  useEffect(() => {
    if (safeVariant === 'intro' && title && description) {
      setAnalyticsEvent({
        _key: `${safeVariant}-${title}-${description}`,
        event: 'select_content',
        content_type: 'banner',
        item_id: `banner-${safeVariant}-view-only`,
        title,
        subtitle: description,
        label_text: '',
        module_name: 'SanityBanner',
        module_variant: safeVariant,
      })
    }
  }, [safeVariant, title, description])

  const videoId = useMemo(() => {
    if (!wistiaShareUrl) return undefined
    const match = wistiaShareUrl.match(/\/medias\/([a-zA-Z0-9]+)/)
    return match?.[1]
  }, [wistiaShareUrl])

  if (!shouldDisplay) {
    return null
  }

  const renderCtaLink = (
    cta: SanityCta | null | undefined,
    buttonVariant: ButtonProps['variant'],
    ctaType: 'primary' | 'secondary' | 'tertiary',
  ) => {
    return cta?.link?.target && cta.link.target.length > 0 ? (
      <SanityLink
        to={cta.link}
        onClick={(_e, normalized) =>
          setAnalyticsEventOnClick(cta, ctaType, normalized, safeVariant)
        }
        buttonVariantOverride={buttonVariant}
      >
        {cta.title}
      </SanityLink>
    ) : null
  }

  let imageElement: React.ReactNode | undefined = undefined

  if (image?.asset?._id) {
    try {
      const imageData = getGatsbyImageData(
        { _ref: `image-${image.asset._id.replace('image-', '')}` },
        { width: 1200 },
        clientConfig.sanity,
      )

      if (imageData) {
        imageElement = <GatsbyImage image={imageData} alt={imageAlt || ''} />
      }
    } catch (error) {
      console.error('Error creating Gatsby image:', error)

      if (image.asset.url) {
        imageElement = <img src={image.asset.url} alt={imageAlt || ''} />
      }
    }
  }

  const videoIframe = params ? (
    <ThirdPartySandboxIFrame
      allowFullScreen
      params={params}
      path="/wistia/index.html"
      sandbox="allow-scripts allow-same-origin"
      title="Banner"
    />
  ) : undefined

  return (
    <Banner
      className={className}
      variant={safeVariant}
      header={header || undefined}
      title={title || ''}
      description={description || undefined}
      image={imageElement}
      imageAlt={imageAlt || undefined}
      primaryCtaChildren={renderCtaLink(primaryCta, 'default', 'primary')}
      secondSectionTitle={secondSectionTitle || undefined}
      secondSectionDescription={secondSectionDescription || undefined}
      secondaryCtaChildren={renderCtaLink(secondaryCta, 'default', 'secondary')}
      tertiaryCtaChildren={renderCtaLink(tertiaryCta, 'secondary', 'tertiary')}
      primaryCtaAriaLabel={primaryCtaAriaLabel || undefined}
      secondaryCtaAriaLabel={secondaryCtaAriaLabel || undefined}
      tertiaryCtaAriaLabel={tertiaryCtaAriaLabel || undefined}
      renderVideoIframe={videoIframe}
      videoCtaText={safeVariant === 'video' ? videoCtaText || 'Watch Video' : undefined}
      onVideoCtaClick={
        safeVariant === 'video'
          ? () => {
              setAnalyticsEvent({
                event: 'select_content',
                content_type: 'banner',
                item_id: `banner-${safeVariant}`,
                title: title || '',
                label_text: videoCtaText || 'Watch Video',
                link_destination: wistiaShareUrl || '',
                video_url: wistiaShareUrl || '',
                video_id: videoId || '',
                module_name: 'SanityBanner',
                module_variant: safeVariant,
              })
            }
          : undefined
      }
    />
  )
}

export default SanityBanner

export const sanityBannerFragment = graphql`
  fragment SanityBannerFragment on SanityBanner {
    variant
    header
    title
    description
    image {
      asset {
        _id
        url
      }
      hotspot {
        x
        y
        height
        width
      }
      crop {
        top
        bottom
        left
        right
      }
    }
    imageAlt
    primaryCta {
      _type
      title
      link {
        ...LinkFragment
      }
    }
    primaryCtaAriaLabel
    secondSectionTitle
    secondSectionDescription
    secondaryCta {
      _type
      title
      link {
        ...LinkFragment
      }
    }
    secondaryCtaAriaLabel
    tertiaryCta {
      _type
      title
      link {
        ...LinkFragment
      }
    }
    tertiaryCtaAriaLabel
    roleBasedContent {
      enabled
      roles
      ...RoleBasedContentFragment
    }
    contextBasedContent {
      ...ContextBasedContentFragment
    }
    wistiaShareUrl
    aspectRatio
    videoCtaText
  }
`
